// src/components/files/FileItem.tsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  alpha,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MovieIcon from '@mui/icons-material/Movie';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import ArticleIcon from '@mui/icons-material/Article';
import TableChartIcon from '@mui/icons-material/TableChart';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import ShareIcon from '@mui/icons-material/Share';
import { useTranslation } from 'react-i18next';
import { useFileManager } from '../../contexts/FileManagerContext';
import { formatFileSize } from '../../services/fileService';
import FolderDialog from './FolderDialog';
import MoveDialog from './MoveDialog';
import ShareDialog from './ShareDialog';

interface FileItemProps {
  type: 'folder' | 'document';
  id: number;
  name: string;
  color?: string | null;
  icon?: string | null;
  mimeType?: string;
  fileSize?: number;
  fileUrl?: string;
  createdAt: string;
  isFavorite?: boolean;
  isSelected: boolean;
  onClick: (e: React.MouseEvent) => void;
  onDoubleClick?: () => void;
  viewMode: 'grid' | 'list';
}

const getFileIconComponent = (mimeType: string | undefined) => {
  if (!mimeType) return <InsertDriveFileIcon />;
  if (mimeType.startsWith('image/')) return <ImageIcon sx={{ color: '#10b981' }} />;
  if (mimeType.startsWith('video/')) return <MovieIcon sx={{ color: '#8b5cf6' }} />;
  if (mimeType.startsWith('audio/')) return <AudiotrackIcon sx={{ color: '#ec4899' }} />;
  if (mimeType === 'application/pdf') return <PictureAsPdfIcon sx={{ color: '#ef4444' }} />;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return <TableChartIcon sx={{ color: '#22c55e' }} />;
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return <SlideshowIcon sx={{ color: '#f59e0b' }} />;
  if (mimeType.includes('document') || mimeType.includes('word')) return <ArticleIcon sx={{ color: '#3b82f6' }} />;
  if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('archive')) return <FolderZipIcon sx={{ color: '#6366f1' }} />;
  return <InsertDriveFileIcon sx={{ color: '#64748b' }} />;
};

const FileItem: React.FC<FileItemProps> = ({
  type,
  id,
  name,
  color,
  icon,
  mimeType,
  fileSize,
  fileUrl,
  createdAt,
  isFavorite = false,
  isSelected,
  onClick,
  onDoubleClick,
  viewMode,
}) => {
  const { t } = useTranslation();
  const { state, deleteFolder, deleteDocument, downloadDocument, toggleFavorite } = useFileManager();

  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleDelete = async () => {
    handleCloseContextMenu();
    if (type === 'folder') {
      await deleteFolder(id);
    } else {
      await deleteDocument(id);
    }
  };

  const handleDownload = async () => {
    handleCloseContextMenu();
    if (type === 'document') {
      await downloadDocument(id, name);
    }
  };

  const handleToggleFavorite = async () => {
    handleCloseContextMenu();
    try {
      await toggleFavorite(id, isFavorite, type);
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const handleRename = () => {
    handleCloseContextMenu();
    setRenameDialogOpen(true);
  };

  const handleMove = () => {
    handleCloseContextMenu();
    setMoveDialogOpen(true);
  };

  const handleShare = () => {
    handleCloseContextMenu();
    setShareDialogOpen(true);
  };

  const folderColor = color || '#2563eb';

  if (viewMode === 'grid') {
    return (
      <>
        <Box
          onClick={onClick}
          onDoubleClick={onDoubleClick}
          onContextMenu={handleContextMenu}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 2,
            borderRadius: 2,
            cursor: 'pointer',
            bgcolor: isSelected ? alpha('#2563eb', 0.08) : 'transparent',
            border: '2px solid',
            borderColor: isSelected ? 'primary.main' : 'transparent',
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: isSelected ? alpha('#2563eb', 0.12) : 'grey.50',
            },
            position: 'relative',
          }}
        >
          {/* Favorite indicator */}
          {isFavorite && (
            <StarIcon
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                fontSize: 16,
                color: '#f59e0b',
              }}
            />
          )}

          {/* Icon */}
          <Box
            sx={{
              width: 64,
              height: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1,
            }}
          >
            {type === 'folder' ? (
              <FolderIcon sx={{ fontSize: 56, color: folderColor }} />
            ) : (
              <Box sx={{ '& > svg': { fontSize: 48 } }}>
                {getFileIconComponent(mimeType)}
              </Box>
            )}
          </Box>

          {/* Name */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              textAlign: 'center',
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </Typography>

          {/* Size for files */}
          {type === 'document' && fileSize !== undefined && (
            <Typography variant="caption" color="text.secondary">
              {formatFileSize(fileSize)}
            </Typography>
          )}
        </Box>

        {/* Context Menu */}
        <Menu
          open={contextMenu !== null}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={contextMenu ? { top: contextMenu.y, left: contextMenu.x } : undefined}
        >
          {type === 'folder' && (
            <MenuItem onClick={onDoubleClick}>
              <ListItemIcon>
                <FolderIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('fileManager.contextMenu.open', 'Ouvrir')}</ListItemText>
            </MenuItem>
          )}

          {type === 'document' && (
            <MenuItem onClick={handleDownload}>
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('fileManager.contextMenu.download', 'Telecharger')}</ListItemText>
            </MenuItem>
          )}

          <Divider />

          {type === 'folder' && (
            <MenuItem onClick={handleRename}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('fileManager.contextMenu.rename', 'Renommer')}</ListItemText>
            </MenuItem>
          )}

          <MenuItem onClick={handleToggleFavorite}>
            <ListItemIcon>
              {isFavorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>
              {isFavorite
                ? t('fileManager.contextMenu.removeFavorite', 'Retirer des favoris')
                : t('fileManager.contextMenu.addFavorite', 'Ajouter aux favoris')}
            </ListItemText>
          </MenuItem>

          <MenuItem onClick={handleMove}>
            <ListItemIcon>
              <DriveFileMoveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('fileManager.contextMenu.move', 'Deplacer')}</ListItemText>
          </MenuItem>

          {type === 'folder' && (
            <MenuItem onClick={handleShare}>
              <ListItemIcon>
                <ShareIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('fileManager.contextMenu.share', 'Partager')}</ListItemText>
            </MenuItem>
          )}

          <Divider />

          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText>{t('fileManager.contextMenu.delete', 'Supprimer')}</ListItemText>
          </MenuItem>
        </Menu>

        {/* Rename dialog */}
        {type === 'folder' && (
          <FolderDialog
            open={renameDialogOpen}
            onClose={() => setRenameDialogOpen(false)}
            mode="rename"
            folderId={id}
            initialName={name}
          />
        )}

        {/* Move dialog */}
        <MoveDialog
          open={moveDialogOpen}
          onClose={() => setMoveDialogOpen(false)}
          itemType={type}
          itemId={id}
          itemName={name}
          currentFolderId={state.currentFolder}
        />

        {/* Share dialog */}
        {type === 'folder' && (
          <ShareDialog
            open={shareDialogOpen}
            onClose={() => setShareDialogOpen(false)}
            folderId={id}
            folderName={name}
          />
        )}
      </>
    );
  }

  // List view
  return (
    <>
      <Box
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        onContextMenu={handleContextMenu}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          borderRadius: 1,
          cursor: 'pointer',
          bgcolor: isSelected ? alpha('#2563eb', 0.08) : 'transparent',
          '&:hover': {
            bgcolor: isSelected ? alpha('#2563eb', 0.12) : 'grey.50',
          },
        }}
      >
        {/* Icon */}
        <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
          {type === 'folder' ? (
            <FolderIcon sx={{ fontSize: 24, color: folderColor }} />
          ) : (
            getFileIconComponent(mimeType)
          )}
        </Box>

        {/* Name */}
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </Typography>

        {/* Size */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ width: 80, textAlign: 'right' }}
        >
          {type === 'document' && fileSize !== undefined ? formatFileSize(fileSize) : '—'}
        </Typography>

        {/* Date */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ width: 120, textAlign: 'right' }}
        >
          {new Date(createdAt).toLocaleDateString()}
        </Typography>

        {/* Favorite */}
        {isFavorite && <StarIcon sx={{ ml: 1, fontSize: 16, color: '#f59e0b' }} />}

        {/* More menu */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleContextMenu(e);
          }}
          sx={{ ml: 1 }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.y, left: contextMenu.x } : undefined}
      >
        {type === 'folder' && (
          <MenuItem onClick={onDoubleClick}>
            <ListItemIcon>
              <FolderIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('fileManager.contextMenu.open', 'Ouvrir')}</ListItemText>
          </MenuItem>
        )}

        {type === 'document' && (
          <MenuItem onClick={handleDownload}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('fileManager.contextMenu.download', 'Telecharger')}</ListItemText>
          </MenuItem>
        )}

        <Divider />

        {type === 'folder' && (
          <MenuItem onClick={handleRename}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('fileManager.contextMenu.rename', 'Renommer')}</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={handleToggleFavorite}>
          <ListItemIcon>
            {isFavorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>
            {isFavorite
              ? t('fileManager.contextMenu.removeFavorite', 'Retirer des favoris')
              : t('fileManager.contextMenu.addFavorite', 'Ajouter aux favoris')}
          </ListItemText>
        </MenuItem>

        <MenuItem onClick={handleMove}>
          <ListItemIcon>
            <DriveFileMoveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('fileManager.contextMenu.move', 'Deplacer')}</ListItemText>
        </MenuItem>

        {type === 'folder' && (
          <MenuItem onClick={handleShare}>
            <ListItemIcon>
              <ShareIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('fileManager.contextMenu.share', 'Partager')}</ListItemText>
          </MenuItem>
        )}

        <Divider />

        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText>{t('fileManager.contextMenu.delete', 'Supprimer')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Rename dialog */}
      {type === 'folder' && (
        <FolderDialog
          open={renameDialogOpen}
          onClose={() => setRenameDialogOpen(false)}
          mode="rename"
          folderId={id}
          initialName={name}
        />
      )}

      {/* Move dialog */}
      <MoveDialog
        open={moveDialogOpen}
        onClose={() => setMoveDialogOpen(false)}
        itemType={type}
        itemId={id}
        itemName={name}
        currentFolderId={state.currentFolder}
      />

      {/* Share dialog */}
      {type === 'folder' && (
        <ShareDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          folderId={id}
          folderName={name}
        />
      )}
    </>
  );
};

export default FileItem;
